import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Directory from "@/pages/Directory";
import OrgChart from "@/pages/OrgChart";
import Messaging from "@/pages/Messaging";
import Announcements from "@/pages/Announcements";
import Incidents from "@/pages/Incidents";
import Knowledge from "@/pages/Knowledge";
import Onboarding from "@/pages/Onboarding";
import Admin from "@/pages/Admin";
import Branding from "@/pages/Branding";
import Metrics from "@/pages/Metrics";
import InviteAccept from "@/pages/InviteAccept";
import Profile from "@/pages/Profile";
import IncidentTemplates from "@/pages/IncidentTemplates";
import TimeOff from "@/pages/TimeOff";
import Shifts from "@/pages/Shifts";
import Tasks from "@/pages/Tasks";
import Kudos from "@/pages/Kudos";
import Polls from "@/pages/Polls";
import Documents from "@/pages/Documents";
import Events from "@/pages/Events";
import Courses from "@/pages/Courses";
import TaskTemplates from "@/pages/TaskTemplates";
import ShiftSwaps from "@/pages/ShiftSwaps";
import Migrate from "@/pages/Migrate";
import TeamManage from "@/pages/TeamManage";
import AuditLog from "@/pages/AuditLog";
import Positions from "@/pages/Positions";
import WelcomeCarousel from "@/pages/WelcomeCarousel";
import { Toaster } from "sonner";

const ADMIN_ONLY = ["admin"];
const ADMIN_OR_MANAGER = ["admin", "manager"];

function LoginGate() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function App() {
  return (
    <ErrorBoundary>
      <BrandingProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-right" />
            <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route
              path="/welcome"
              element={
                <ProtectedRoute>
                  <WelcomeCarousel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="metrics" element={<Metrics />} />
              <Route path="profile" element={<Profile />} />
              <Route path="timeoff" element={<TimeOff />} />
              <Route path="shifts" element={<Shifts />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="kudos" element={<Kudos />} />
              <Route path="polls" element={<Polls />} />
              <Route path="documents" element={<Documents />} />
              <Route path="events" element={<Events />} />
              <Route path="courses" element={<Courses />} />
              <Route path="task-templates" element={<TaskTemplates />} />
              <Route path="shift-swaps" element={<ShiftSwaps />} />
              <Route path="directory" element={<Directory />} />
              <Route path="org" element={<OrgChart />} />
              <Route path="messages" element={<Messaging />} />
              <Route path="announcements" element={<Announcements />} />
              <Route path="incidents" element={<Incidents />} />
              <Route path="knowledge" element={<Knowledge />} />
              <Route path="onboarding" element={<Onboarding />} />
              <Route
                path="admin"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="branding"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <Branding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="migrate"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <Migrate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="team"
                element={
                  <ProtectedRoute roles={ADMIN_OR_MANAGER}>
                    <TeamManage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="audit-log"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <AuditLog />
                  </ProtectedRoute>
                }
              />
              <Route
                path="positions"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <Positions />
                  </ProtectedRoute>
                }
              />
              <Route
                path="incident-templates"
                element={
                  <ProtectedRoute roles={ADMIN_ONLY}>
                    <IncidentTemplates />
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </BrandingProvider>
    </ErrorBoundary>
  );
}

export default App;
